import { FC, useState } from "react";
import { useBucketContext } from "context/bucket";
import { Modal, Button, Card } from "react-bootstrap";
import { showErrorModal } from "redux/slices";
import { useAppDispatch } from "redux/store";
import { FetchContent } from "./fetch-content";
import { FolderTree, TreeNode } from "./folderTree";
import { ModalProps } from "../../types";
import { convertContentsToFolderList } from "./utils";
import { ConfirmationModal } from "components/confirmationModal";
import { showErrorToast, showSuccessToast } from "utils";

export const MoveModal: FC<ModalProps> = (props) => {
  const { config, onClose } = props;
  const { breadcrumb, refreshContents, renameFolder, renameFile } =
    useBucketContext();
  const [selectedNode, setSelectedNode] = useState<TreeNode | null>(null);
  const [elements, setElements] = useState<string[] | undefined>(undefined);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const dispatch = useAppDispatch();

  const handleFolderSelect = (node: TreeNode) => setSelectedNode(node);

  const executeMovement = async () => {
    setShowConfirmationModal(false);
    if (selectedNode && config) {
      const result = config.isFolder
        ? await renameFolder?.(
            `${selectedNode.fullPath}${config.name}/`,
            config.path
          )
        : await renameFile?.(
            `${selectedNode.fullPath}${config.name}`,
            config.path,
            true
          );
      return result;
    }
    return false;
  };

  const handleResult = (result: boolean) => {
    if (result) {
      refreshContents();
      showSuccessToast(
        config?.isFolder ? "Cartella spostata" : `File spostato`,
        `move-${config?.isFolder ? "folder" : "file"}-toast`
      );
      onClose();
    } else {
      if (config) {
        const { isFolder } = config;
        showErrorToast(
          `C'è stato un errore spostando ${
            isFolder ? "la cartella" : "il file"
          }`,
          `error-${isFolder ? "folder" : "file"}-move`
        );
        dispatch(
          showErrorModal({
            text: `Non stai spostando la cartella`,
          })
        );
      }
    }
    setSelectedNode(null);
  };

  const folderChecks = (): boolean => {
    if (config && selectedNode) {
      if (selectedNode.fullPath === config.path) {
        dispatch(
          showErrorModal({
            text: `Non stai spostando la cartella`,
          })
        );
        return false;
      }
      const subFoldersList = elements?.filter(
        (f) => f.indexOf(config.path) >= 0
      );
      const checkedData = subFoldersList?.filter(
        (obj) => obj.indexOf(selectedNode.name) >= 0
      );
      if (checkedData?.length) {
        dispatch(
          showErrorModal({
            text: "Non puoi spostare una cartella in una sua sottocartella",
          })
        );
        return false;
      }
      const data = selectedNode.children?.filter((f) => f.name === config.name);
      if (data && data.length) {
        setShowConfirmationModal(true);
        return false;
      }
      return true;
    }
    return false;
  };

  const fileChecks = (): boolean => {
    if (config && selectedNode) {
      const destinationPath = `${selectedNode?.fullPath}${config?.name}`;
      if (destinationPath === config?.path) {
        dispatch(
          showErrorModal({
            text: `Non stai spostando il file`,
          })
        );
        return false;
      }
      const data = elements?.find((o) => o === destinationPath);
      if (data && data.length) {
        setShowConfirmationModal(true);
        return false;
      }
      return true;
    }
    return false;
  };

  const move = async () => {
    const result = await executeMovement();
    handleResult(result);
  };

  const handleMoveFolder = async () => {
    if (config && selectedNode) {
      const passed = config.isFolder ? folderChecks() : fileChecks();
      if (passed) {
        const result = await executeMovement();
        handleResult(result);
      }
    }
  };

  return (
    <>
      <Modal
        show={Boolean(config)}
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header className="d-flex justify-content-center align-items-center border-bottom-0">
          <Modal.Title className="text-light fs-2 fw-bold text-center">
            Sposta in
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Card>
            <Card.Body>
              {elements ? (
                <FolderTree
                  structure={
                    elements.length
                      ? convertContentsToFolderList(elements ?? [])[0]
                      : { name: "Home", children: [], fullPath: breadcrumb }
                  }
                  selected={selectedNode}
                  handleFolderSelect={handleFolderSelect}
                />
              ) : (
                <FetchContent setElements={setElements} />
              )}
            </Card.Body>
          </Card>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-center align-items-center border-top-0 mt-2">
          <Button
            variant="outline-light px-4 rounded-1"
            onClick={onClose}
            style={{ width: 130 }}
          >
            Annulla
          </Button>
          <Button
            variant="secondary text-white px-4 rounded-1"
            onClick={handleMoveFolder}
            style={{ width: 130 }}
            disabled={!Boolean(selectedNode)}
          >
            Sposta
          </Button>
        </Modal.Footer>
      </Modal>
      <ConfirmationModal
        show={showConfirmationModal}
        text={`Esiste già ${
          config?.isFolder ? "una cartella" : "un file"
        } con lo stesso nome nella cartella selezionata`}
        title="Sei sicuro?"
        confirmButton="Si, sovrascrivi!"
        onConfirm={move}
        onCancel={() => setShowConfirmationModal(false)}
      />
    </>
  );
};
